/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { SavableObject } from '../../components/SaveDataComponents';
import { SharedWorldComponent } from '../../components/MultiplayerComponents';
import { System } from 'ecsy';
import { getOnlyEntity } from '../../utils/entityUtils';
import { savePlotPlants } from '../../lib/firebase/PlotStorage';

// Wait this many seconds after the last change before pushing to Firestore, so
// a burst of planting collapses into a single write.
const SAVE_DEBOUNCE = 4;

/**
 * Persists the player's own plot to the shared world (design Phase 3, save side).
 *
 * Whenever a SavableObject entity (a plant) is added or removed, the plot is
 * marked dirty and — after a short debounce — the full plant list is serialized
 * (reusing the existing save format) and written to Firestore under the player's
 * plot. This is the shared-world counterpart of LocalSaveDataSystem's localForage
 * save; the two run side by side, so the garden is kept both locally and in the
 * cloud.
 *
 * Dormant unless the shared world is live (Firebase configured + signed in with
 * a plot allocated). While dormant it does nothing and the game persists locally
 * as before.
 */
export class GardenSyncSystem extends System {
	init() {
		this._dirty = false;
		this._dirtySince = 0;
		this._saving = false;

		// Best-effort final flush when the tab closes. Firestore's write may not
		// complete during unload, but the debounced save above covers the common
		// case; this just narrows the window for losing the last change.
		this._onBeforeUnload = () => {
			const plotId = this._readyPlotId();
			if (plotId && this._dirty) {
				this._flush(plotId);
			}
		};
		window.addEventListener('beforeunload', this._onBeforeUnload);
	}

	/** @returns {string|null} the player's plot id if the shared world is live. */
	_readyPlotId() {
		const entity = getOnlyEntity(this.queries.sharedWorld, false);
		const sharedWorld = entity
			? entity.getComponent(SharedWorldComponent)
			: null;
		if (
			!sharedWorld ||
			!sharedWorld.configured ||
			!sharedWorld.ready ||
			!sharedWorld.plotId
		) {
			return null;
		}
		return sharedWorld.plotId;
	}

	execute(_delta, time) {
		// Drain the reactive query every frame so added/removed don't accumulate
		// across frames even while the shared world is dormant.
		const changed =
			this.queries.saveObjects.added.length > 0 ||
			this.queries.saveObjects.removed.length > 0;

		const plotId = this._readyPlotId();
		if (!plotId) return;

		if (changed) {
			this._dirty = true;
			this._dirtySince = time;
		}

		if (
			this._dirty &&
			!this._saving &&
			time - this._dirtySince >= SAVE_DEBOUNCE
		) {
			this._flush(plotId);
		}
	}

	async _flush(plotId) {
		this._saving = true;
		this._dirty = false;
		const plants = this._serializePlants();
		try {
			await savePlotPlants(plotId, plants);
			console.info(
				`[SharedWorld] Saved ${plants.length} plant(s) to plot ${plotId}.`,
			);
		} catch (e) {
			console.error('[SharedWorld] Failed to save plot to Firestore', e);
			this._dirty = true; // let the next debounce retry.
		} finally {
			this._saving = false;
		}
	}

	/**
	 * Serialize every SavableObject into the existing save format
	 * (`[{ ComponentName: {...} }, ...]`), matching LocalSaveDataSystem so the
	 * cloud and local formats stay identical.
	 */
	_serializePlants() {
		const objects = [];
		this.queries.saveObjects.results.forEach((entity) => {
			const components = entity.getComponents();
			const obj = {};
			for (const key in components) {
				const component = components[key];
				if (!component.serialize) continue;
				obj[component.getName()] = component.serialize();
			}
			objects.push(obj);
		});
		return objects;
	}
}

GardenSyncSystem.queries = {
	sharedWorld: { components: [SharedWorldComponent] },
	saveObjects: {
		components: [SavableObject],
		listen: { added: true, removed: true },
	},
};
